import React, { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Space, Button as AntButton } from "antd";
import {
    CaretDownOutlined,
    CaretUpOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    DownloadOutlined,
    EyeFilled,
    EyeOutlined,
    SaveOutlined,
} from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";

import TableExporter from "../../shared/TableExporter/TableExporter";
import { GenericRecord } from "scripts/models/GenericRecord";
import { IViewsKeys } from "scripts/models/User";
import { TableColumns } from "../../../columns/ColumnConfig";
import { TableColumnsSelector } from "../../shared/TableColumnsSelector/TableColumnsSelector";
import { TableExportType } from "../../../../scripts/csv/exporters/Exporters";
import { UserSettingsContext } from "../../../../scripts/context/UserSettingsProvider";
import { Switch, Tab, Tabs, Button, Badge, Popover, PopoverTrigger, PopoverContent, Tooltip } from "@heroui/react";

export interface JournalProps {
    toJournal?: number;
    onJournalClicked?: () => void;
}

export const JournalButton: FC<JournalProps> = memo(function JournalButton(props) {
    const { formatMessage } = useIntl();
    const title = (
        <section>
            <b>Journal</b>
            {props.toJournal === 0 && <div>{formatMessage({ id: "app.kb.journal.no_items_tooltip" })}</div>}
        </section>
    );
    if (!props.toJournal) {
        return (
            <Tooltip placement="bottom-end" content={title} classNames={{ content: "max-w-sm" }}>
                <Button startContent={<SaveOutlined />} variant={"ghost"}>
                    <span className="Toolbar__LongLabel">Journal</span>
                </Button>
            </Tooltip>
        );
    }
    return (
        <Popover placement={"bottom-end"} showArrow backdrop={"blur"}>
            <Badge content={props.toJournal}>
                <PopoverTrigger>
                    <Button startContent={<SaveOutlined />} variant={"ghost"} color={"primary"}>
                        <span className="Toolbar__LongLabel">Journal</span>
                    </Button>
                </PopoverTrigger>
            </Badge>
            <PopoverContent>
                <div className="px-1 py-2">
                    <div className="font-bold py-2">
                        <span>Journal</span>: <FormattedMessage id="app.confirmation.header" />
                    </div>
                    <div style={{ maxWidth: 400 }}>
                        <Button color={"primary"}>
                            <FormattedMessage id="app.button.confirm" />
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
});

export interface EyeButtonProps {
    showRemoved?: boolean;
    onToggleHidden?: () => void;
}

export const ShowRemovedSwitch: React.FC<EyeButtonProps> = ({ showRemoved, onToggleHidden }) => {
    return (
        <Switch
            isSelected={showRemoved}
            onValueChange={onToggleHidden}
            thumbIcon={({ isSelected, className }) =>
                isSelected ? <EyeFilled className={className} /> : <EyeOutlined className={className} />
            }
        >
            entfernte Buchungen anzeigen
        </Switch>
    );
};

export type PaymentStatusSelectorType = "avis" | "offen" | "bezahlt" | null;

export type PaymentStatusSelectorProps = {
    paymentStatusValue?: PaymentStatusSelectorType;
    onPaymentStatusChange?: (v: PaymentStatusSelectorType) => void;
    useAvis?: boolean;
};
export const PaymentStatusSelector: React.FC<PaymentStatusSelectorProps> = memo(
    ({ paymentStatusValue, onPaymentStatusChange, useAvis }) => {
        const [value, setValue] = useState<PaymentStatusSelectorType>(paymentStatusValue);

        const onChange = useCallback(
            (v: PaymentStatusSelectorType) => {
                setValue(v);
                setTimeout(() => onPaymentStatusChange(v), 300);
            },
            [onPaymentStatusChange]
        );
        useEffect(() => {
            setValue(paymentStatusValue);
        }, [paymentStatusValue]);
        const options = useMemo(() => {
            return [
                { value: "all", label: "Alle" },
                useAvis ? { value: "avis", label: "Avis" } : null,
                { value: "offen", label: "offen" },
                { value: "bezahlt", label: "bezahlt" },
            ].filter(Boolean);
        }, [useAvis]);
        return (
            <Tabs
                aria-label="Payment Status Filter"
                selectedKey={value ?? "all"}
                onSelectionChange={(key: string) => onChange(key === "all" ? null : (key as PaymentStatusSelectorType))}
            >
                {options.map(option => (
                    <Tab key={option.value} title={option.label} />
                ))}
            </Tabs>
        );
    }
);

export type ReviewStatusSelectorType = GenericRecord["review"] | "new";

export type ReviewStatusSelectorProps = {
    reviewStatusValue?: ReviewStatusSelectorType;
    onReviewStatusChange?: (v: ReviewStatusSelectorType) => void;
};
export const ReviewStatusSelector: React.FC<ReviewStatusSelectorProps> = memo(
    ({ reviewStatusValue, onReviewStatusChange }) => {
        const [value, setValue] = useState<ReviewStatusSelectorType>(reviewStatusValue);

        const onChange = useCallback(
            (v: ReviewStatusSelectorType) => {
                setValue(v);
                setTimeout(() => onReviewStatusChange(v), 300);
            },
            [onReviewStatusChange]
        );
        useEffect(() => {
            setValue(reviewStatusValue);
        }, [reviewStatusValue]);
        const options = useMemo(() => {
            const statuses: ReviewStatusSelectorType[] = [null, "new", "ok", "changed", "error", "question"];
            return statuses.map(value => ({
                value,
                label: value ? <FormattedMessage id={`app.fields.recordReview.${value ?? "new"}`} /> : "Alle",
            }));
        }, []);
        return (
            <Tabs
                aria-label="Review Status Filter"
                selectedKey={value ?? "all"}
                onSelectionChange={(key: string) => onChange(key === "all" ? null : (key as ReviewStatusSelectorType))}
            >
                {options.map(option => (
                    <Tab key={option.value ?? "all"} title={option.label} />
                ))}
            </Tabs>
        );
    }
);

export interface AnsichtProps {
    tableColumns?: TableColumns<any>;
    view?: keyof IViewsKeys;
    disabledOptions?: string[];
}

export const AnsichtBtn: FC<AnsichtProps> = React.memo(function AnsichtBtn({ tableColumns, view, disabledOptions }) {
    const { onChangeDisabledColumns, disabledColumns } = React.use(UserSettingsContext);
    const intl = useIntl();

    const columnNames = React.useMemo(
        () => tableColumns.getViewableColumnNames(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tableColumns, intl.locale]
    );

    return (
        <TableColumnsSelector
            columns={columnNames}
            unselectedColumns={disabledColumns.get(view)}
            disabledOptions={disabledOptions}
            onChange={list => onChangeDisabledColumns(view, list)}
        />
    );
});

export interface IExportProps {
    exportColumns: string[];
    onDownload?: (type: TableExportType, columns?: string[], timeframe?: { from?: Date; to?: Date }) => Promise<void>;
    tableColumns?: TableColumns<GenericRecord>;
}

export const ExportBtn: FC<IExportProps> = ({ exportColumns, onDownload, tableColumns }) => {
    const columns = tableColumns.getColumnConfig().map(v => v.key);

    const handleDownload = async (type: TableExportType, newColumns: string[]) => {
        if (onDownload) {
            await onDownload(type, newColumns);
        }
    };

    return (
        <TableExporter columns={columns} onOk={handleDownload} defaultColumnsKeys={exportColumns} className="ExportBtn">
            <Button radius="full" variant={"ghost"}>
                <DownloadOutlined />
            </Button>
        </TableExporter>
    );
};

export interface ConfirmProps {
    toConfirm?: number;
    onConfirmClicked?: () => void;
}

export const ConfirmButton: FC<ConfirmProps> = memo(function ConfirmButton(props) {
    const { formatMessage } = useIntl();
    const text = formatMessage({ id: "app.button.confirm" });
    return (
        <Badge content={props.toConfirm} color={"success"} isInvisible={!props.toConfirm}>
            <Button
                // type="primary"
                onPress={props.onConfirmClicked}
                startContent={<CheckCircleOutlined />}
                isDisabled={!props.toConfirm}
                variant={"ghost"}
                color={props.toConfirm ? "primary" : "default"}
            >
                <span className="Toolbar__LongLabel">{text}</span>
            </Button>
        </Badge>
    );
});

export interface PrioritySectionProps {
    // toSort?: number;
    // onSortClicked?: () => void;
    toMoveItems?: GenericRecord[];
    toMovePrev?: GenericRecord;
    onMoveUpClick?: (vs: GenericRecord[], prev: GenericRecord) => void;
    toMoveNext?: GenericRecord;
    onMoveDownClick?: (vs: GenericRecord[], next: GenericRecord) => void;
    onMoveOkClick?: () => void;
}

export const PrioritySection: FC<PrioritySectionProps> = memo(function PrioritySection(props) {
    const onMoveUp = () => {
        props.onMoveUpClick(props.toMoveItems, props.toMovePrev);
    };
    const onMoveDown = () => {
        props.onMoveDownClick(props.toMoveItems, props.toMoveNext);
    };

    return (
        <Space.Compact>
            <AntButton disabled={!props.toMovePrev} icon={<CaretUpOutlined />} onClick={onMoveUp} />
            <AntButton disabled={!props.toMoveNext} icon={<CaretDownOutlined />} onClick={onMoveDown} />
            <AntButton
                type="primary"
                disabled={props.toMoveItems.length === 0}
                icon={<CheckOutlined />}
                onClick={props.onMoveOkClick}
            />
            {/* <Button type="primary" onClick={props.onSortClicked} icon={<SwapOutlined />} disabled={props.toSort === 0}> */}
            {/*     Auto ({props.toSort}) */}
            {/* </Button> */}
        </Space.Compact>
    );
});
